import { TILES_BASE_URL } from '@/lib/env';
import type { StyleSpecification } from 'maplibre-gl';

// Vector / raster basemap for the Intel map — proxied through tile-edge.
//
// **Architecture (Iron Rule #1 — no third-party in the data path):**
// Every tile request the SPA makes goes through `tiles.eidollon.app/v1/...`.
// tile-edge handles auth (JWT), audit (per-tenant emit), and provider
// abstraction (GovCloud cutover = single env-var swap, client unchanged).
//
// Variants:
//   * 'dark'    — Protomaps dark vector (default in dev; always cached locally).
//   * 'terrain' — Protomaps vector + glyphs for road/place labels (T1 wired
//                 `/v1/fonts/{fontstack}/{range}.pbf`).
//   * 'sat'     — Esri World Imagery raster passthrough via tile-edge
//                 (`/v1/esri-imagery/{z}/{x}/{y}`, T2 in flight).
//
// Each variant returns a complete StyleSpecification — switch by `variant`
// at the call site, MapLibre `setStyle` handles the swap.

export type BasemapVariant = 'terrain' | 'sat' | 'dark';

export interface BasemapStyleOptions {
  /** Tile-edge origin. Defaults to the env-configured value. */
  tileBase?: string;
  /** Which basemap variant to build. */
  variant?: BasemapVariant;
}

function resolveBase(opts: BasemapStyleOptions): string {
  const raw = (opts.tileBase ?? TILES_BASE_URL).replace(/\/+$/, '');
  // MapLibre requires absolute URLs in the `tiles` array — relative paths
  // are silently ignored and no tile requests are made. Resolve against the
  // current origin so `/api/tiles` → `http://host/api/tiles`.
  if (raw.startsWith('http')) return raw;
  if (typeof window !== 'undefined') return `${window.location.origin}${raw}`;
  return raw;
}

/* ────────────────────────────────────────────────────────────────────
 *  Dark vector — the original style, always available, locally cached.
 *  Used as the auto-fallback when terrain/sat fail to fetch.
 * ──────────────────────────────────────────────────────────────────── */
function buildDarkStyle(base: string): StyleSpecification {
  return {
    version: 8,
    name: 'Eidollon Dark Vector',
    sources: {
      protomaps: {
        type: 'vector',
        tiles: [`${base}/v1/protomaps-dark/{z}/{x}/{y}.pbf`],
        minzoom: 0,
        maxzoom: 14,
        attribution: '© OpenStreetMap contributors · Protomaps',
      },
    },
    layers: [
      { id: 'background', type: 'background', paint: { 'background-color': '#0a1018' } },
      {
        id: 'earth',
        type: 'fill',
        source: 'protomaps',
        'source-layer': 'earth',
        paint: { 'fill-color': '#161c26' },
      },
      {
        id: 'water',
        type: 'fill',
        source: 'protomaps',
        'source-layer': 'water',
        paint: { 'fill-color': '#070b12' },
      },
      {
        id: 'boundaries',
        type: 'line',
        source: 'protomaps',
        'source-layer': 'boundaries',
        paint: { 'line-color': '#5a6470', 'line-width': 1.0, 'line-opacity': 0.9 },
      },
    ],
  };
}

/* ────────────────────────────────────────────────────────────────────
 *  Terrain — dark vector + labeled roads + place labels via glyphs.
 *  Glyphs route (T1): `/v1/fonts/{fontstack}/{range}.pbf`.
 *  Fontstack: "Noto Sans Regular" (body) / "Noto Sans Medium" (places).
 * ──────────────────────────────────────────────────────────────────── */
function buildTerrainStyle(base: string): StyleSpecification {
  return {
    version: 8,
    name: 'Eidollon Terrain',
    glyphs: `${base}/v1/fonts/{fontstack}/{range}.pbf`,
    sources: {
      protomaps: {
        type: 'vector',
        tiles: [`${base}/v1/protomaps-dark/{z}/{x}/{y}.pbf`],
        minzoom: 0,
        maxzoom: 14,
        attribution: '© OpenStreetMap contributors · Protomaps',
      },
    },
    layers: [
      // Terrain reads as a distinct, slightly warmer/lighter landmass than the
      // pure-dark variant so the two are tellable apart at ANY zoom — not just
      // once road/label layers kick in (the old bug: terrain ≡ dark at world
      // view because their earth/water/background were identical and labels
      // only appeared at z3+).
      { id: 'background', type: 'background', paint: { 'background-color': '#0c1410' } },
      {
        id: 'earth',
        type: 'fill',
        source: 'protomaps',
        'source-layer': 'earth',
        paint: { 'fill-color': '#232c22' },
      },
      {
        id: 'water',
        type: 'fill',
        source: 'protomaps',
        'source-layer': 'water',
        paint: { 'fill-color': '#0a1622' },
      },
      {
        id: 'boundaries',
        type: 'line',
        source: 'protomaps',
        'source-layer': 'boundaries',
        paint: { 'line-color': '#6b7560', 'line-width': 1.0, 'line-opacity': 0.9 },
      },
      // Road casing — thin grey, suppressed below z6 to avoid clutter.
      {
        id: 'roads',
        type: 'line',
        source: 'protomaps',
        'source-layer': 'roads',
        minzoom: 6,
        paint: { 'line-color': '#4a5240', 'line-width': 0.6, 'line-opacity': 0.7 },
      },
      // Place labels — symbol layer reads from `glyphs` URL above.
      //
      // Three label defects this config fixes (all reported live):
      //   * Overlap / clutter — explicit declutter (text-allow-overlap:false)
      //     plus padding so adjacent labels don't kiss.
      //   * Flicker — without a stable sort key, MapLibre's collision pass
      //     can pick a different winner each frame while panning, so labels
      //     pop in and out. Sort by `min_zoom` (lower = more important) so
      //     the winner is deterministic and major places always win.
      //   * Tofu on non-Latin names — we vendor only the Latin Noto Sans
      //     glyph ranges, so a CJK/Arabic/Hebrew local `name` renders as
      //     boxes. The Protomaps v4 places layer carries a `name:en` for
      //     every place; verified against the live build it is populated
      //     (never an empty string) for ~99% of features, so we prefer it
      //     and fall back to the local `name` only when `name:en` is
      //     absent. English-first also matches the product's US-federal
      //     audience. (The rare fallback is already Latin in almost every
      //     case, so this removes the tofu without ever blanking a label.)
      {
        id: 'places',
        type: 'symbol',
        source: 'protomaps',
        'source-layer': 'places',
        minzoom: 2,
        layout: {
          'text-field': ['coalesce', ['get', 'name:en'], ['get', 'name']],
          'text-font': ['Noto Sans Medium'],
          'text-size': 11,
          'text-letter-spacing': 0.05,
          'text-max-width': 8,
          'text-padding': 6,
          'text-allow-overlap': false,
          'symbol-sort-key': ['coalesce', ['get', 'min_zoom'], 15],
        },
        paint: {
          'text-color': '#c8d4e2',
          'text-halo-color': '#0a1018',
          'text-halo-width': 1.2,
        },
      },
    ],
  };
}

/* ────────────────────────────────────────────────────────────────────
 *  Sat — Esri World Imagery raster passthrough.
 *  Route (T2 in flight): `/v1/esri-imagery/{z}/{x}/{y}`.
 *  No glyphs — labels would clash with the imagery; intel overlays carry
 *  the readable type. Time-travel Sentinel-2 layered on top by MapView.
 * ──────────────────────────────────────────────────────────────────── */
function buildSatStyle(base: string): StyleSpecification {
  return {
    version: 8,
    name: 'Eidollon Satellite',
    glyphs: `${base}/v1/fonts/{fontstack}/{range}.pbf`,
    sources: {
      'esri-imagery': {
        type: 'raster',
        tiles: [`${base}/v1/esri-imagery/{z}/{x}/{y}`],
        tileSize: 256,
        minzoom: 0,
        maxzoom: 19,
        attribution: 'Tiles © Esri — World Imagery',
      },
    },
    layers: [
      { id: 'background', type: 'background', paint: { 'background-color': '#0a1018' } },
      { id: 'sat', type: 'raster', source: 'esri-imagery' },
    ],
  };
}

export function buildStyle(opts: BasemapStyleOptions = {}): StyleSpecification {
  const base = resolveBase(opts);
  const variant: BasemapVariant = opts.variant ?? 'terrain';
  switch (variant) {
    case 'dark':
      return buildDarkStyle(base);
    case 'sat':
      return buildSatStyle(base);
    default:
      // 'terrain' + any unknown value fall back to the labeled terrain style.
      return buildTerrainStyle(base);
  }
}

/** @deprecated use `buildStyle({ variant: 'dark' })` — kept temporarily for
 *  callers that haven't been migrated. */
export function buildDarkVectorStyle(opts: BasemapStyleOptions = {}): StyleSpecification {
  return buildStyle({ ...opts, variant: 'dark' });
}
